import { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useStateContext } from '../context/StateContext';
import { _CREATE } from '../utils/patches';
import { useAuthContext } from '../whitaijiFrontendLibrary/auth/hooks/useAuthContext';
import { AuthContext } from '../context/AuthContext';
import { generateMongoDBId } from '../whitaijiFrontendLibrary/lib/utils'

/**
 * Custom hook for song logging functionality
 * @param {Object} options - Configuration options
 * @param {string} options.songId - ID of the song being played
 * @param {boolean} options.isPlaying - Whether the song is currently playing
 * @param {number} options.minListenTime - Minimum consecutive listening time in seconds before logging (default: 30)
 * @param {Function} options.onLogSuccess - Callback function when logging is successful
 * @param {Function} options.onLogFailed - Callback function when logging fails
 * @param {Object} options.audioController - Audio controller instance (optional)
 * @returns {Object} - Song logging state and controls
 */
const useSongLogger = ({ 
  songId, 
  isPlaying, 
  minListenTime = 30,
  onLogSuccess = ()=>{},
  onLogFailed = ()=>{},
  audioController
}) => {
  const { apiUrl } = useStateContext();
  const {user} = useAuthContext(AuthContext) 
  const queryClient = useQueryClient();
 
  const [songLogTime, setSongLogTime] = useState(0);
  const [songLogSubmitted, setSongLogSubmitted] = useState(false);
  const intervalRef = useRef(null);

  const songLogDoc = { 
    songId,
    _id: generateMongoDBId(),
    //listenTime: songLogTime || minListenTime
    user_id: user?._id
  };

  // Error and success handlers for API calls
  const onError = (error, data={}) => {
    const{ route, doc } = data;
    process.env.NODE_ENV === "development" && console.error('Error logging song:', error);
    if(onLogFailed)onLogFailed(doc)
  };
  
  const onSuccess = (data) => {
    process.env.NODE_ENV === "development" && console.log('Song logged successfully:', data);
    if (onLogSuccess) onLogSuccess(data);
  };
  
  // Song logging mutation
  const { mutate: logSong } = useMutation({
    mutationFn: async(doc) => {
      process.env.NODE_ENV === "development" && console.log('useSongLoggerCalling _CREATE with doc:', doc);
      return await _CREATE({
        route: `${apiUrl}/song-logs`, 
        doc: doc, 
        user, 
        onSuccess, 
        onError
      });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['songLog'] }),
    mutationKey: ['songLog'],
  });

  // Setup seek event listener if audioController is provided
  useEffect(() => {
    if (!audioController) return;
    
    // Function to handle seek events
    const handleSeek = () => {
      process.env.NODE_ENV === "development" && console.log('Seek event detected - resetting song log timer');
      setSongLogTime(0);
      setSongLogSubmitted(false);
    };
    
    // If audioController has a seek method, we can potentially hook into it
    // This is a placeholder for any future implementation of seek event handling
    // with the audioController
    
    return () => {
      // Cleanup if needed
    };
  }, [audioController]);

  // Track consecutive listening time
  useEffect(() => {
    if (isPlaying && !songLogSubmitted) {
      // Debug logs
      process.env.NODE_ENV === "development" && console.log('Starting song log timer for song ID:', songId);
      process.env.NODE_ENV === "development" && console.log('User ID:', user?._id);
      process.env.NODE_ENV === "development" && console.log('API URL:', apiUrl);
      
      // Start timer to track consecutive listening time
      intervalRef.current = setInterval(() => {
        setSongLogTime(prev => {
          const newTime = prev + 1;
          process.env.NODE_ENV === "development" && console.log('Song log time:', newTime, 'seconds of ', minListenTime);
          
          // If we've reached the minimum listening time, log the song
          if (newTime >= minListenTime && !songLogSubmitted && songId) {
            process.env.NODE_ENV === "development" && console.log('Logging song after', newTime, 'seconds of consecutive listening');
            try {
              // Create the document to log
              
              process.env.NODE_ENV === "development" && console.log('Song log document:', songLogDoc);
              
              // Call the mutation function
              logSong(songLogDoc);
              setSongLogSubmitted(true);
              clearInterval(intervalRef.current);
            } catch (error) {
              process.env.NODE_ENV === "development" && console.error('Error in song logging:', error);
            }
          } else if (newTime >= minListenTime && !songId) {
            process.env.NODE_ENV === "development" && console.error('Cannot log song: No song ID provided');
          }
          
          return newTime;
        });
      }, 1000);
    } else {
      // Clear interval when paused
      clearInterval(intervalRef.current);
    }
    
    return () => {
      clearInterval(intervalRef.current);
    };
  }, [isPlaying, songLogSubmitted, songId, minListenTime, user]);

  // Reset when song changes
  useEffect(() => {
    setSongLogTime(0);
    setSongLogSubmitted(false);
  }, [songId]);

  // Manual log function if needed
  const manualLogSong = () => {
    if (songId && !songLogSubmitted) {
      
      logSong(songLogDoc);
      setSongLogSubmitted(true);
    }
  };

  return {
    songLogTime,
    songLogSubmitted,
    manualLogSong,
    songLogDoc,
    resetSongLog: () => {
      setSongLogTime(0);
      setSongLogSubmitted(false);
    }
  };
};

export default useSongLogger;
